import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { defaultLanguage, translations } from './translations';

const LanguageContext = createContext();

// Exported so class components (e.g. error boundaries) can use static contextType.
export { LanguageContext };

const STORAGE_KEY = 'partybuddy.language';

// Only the language preference itself is persisted here — no credentials or
// user data — so the choice survives refresh, navigation, logout/login, and
// reopening the browser (localStorage, not sessionStorage).
const getStoredLanguage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && translations[stored] ? stored : null;
  } catch {
    // Storage may be unavailable (private mode / disabled) — fall back silently.
    return null;
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => getStoredLanguage() || defaultLanguage);

  const setLanguage = (next) => {
    setLanguageState(next);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Ignore storage failures — language still works for the current session.
    }
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t: (path, fallback = '') => {
      const parts = path.split('.');
      let current = translations[language] || translations[defaultLanguage];
      for (const part of parts) {
        current = current?.[part];
        if (!current) return fallback;
      }
      return current;
    },
    labels: translations[language] || translations[defaultLanguage]
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  return useContext(LanguageContext);
}
